class ColorSwatch extends HTMLElement {
  constructor() {
    super();

    this.cached = {};
    this.variantId = this.dataset.variantId;
    this.container = this.closest('.card-wrapper');

    // Set accessibility attributes
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this.setAttribute('aria-label', `Select color ${this.getAttribute('title')}`);

    if (this.variantId || theme.config.mqlSmall) {
      this.addEventListener('mouseenter', this.onHoverHandler.bind(this));
      this.addEventListener('click', this.onClickHandler.bind(this));
      this.querySelector('a')?.addEventListener('click', (event) => event.preventDefault());
    } else {
      this.addEventListener('mouseenter', this.onHoverHandler.bind(this));
    }
  }

  onClickHandler(event) {
    if (theme.config.mqlSmall || theme.config.isTouch) {
      event.preventDefault();
      this.onHoverHandler();
    }
  }

  onHoverHandler() {
    if (this.variantId) {
      theme.config.mqlSmall || theme.config.isTouch ? this.colorSwatchChange() : this.colorSwatchFetch();
    } else {
      this.colorSwatchChange();
    }
  }

  colorSwatchChange(forceUpdate = false) {
    const image = this.container.querySelector('.card--image-animate.media img');
    const pictureSource = this.container.querySelector('.card--image-animate.media source');

    if (image !== null || pictureSource != null || forceUpdate) {
      if (image !== null) {
        image.src = this.dataset.src;
        image.srcset = this.dataset.srcset;
        image.alt = this.getAttribute('title') || image.alt;
      }

      if (pictureSource !== null) {
        pictureSource.srcset = this.dataset.srcset;
      }

      const swatches = this.container.querySelectorAll('.color-swatch');
      swatches.forEach((swatch) => {
        swatch.classList.remove('is-active');
        swatch.setAttribute('aria-selected', 'false');
      });

      this.classList.add('is-active');
      this.setAttribute('aria-selected', 'true');

      // Update color label
      const colorLabel = this.container.querySelector('.color-swatch-label');
      if (colorLabel) {
        colorLabel.textContent = this.getAttribute('title');
      }

      // Handle media visibility
      const mediaContainer = this.container.querySelector('.card--image-animate.media');
      if (mediaContainer) mediaContainer.style.display = 'block';
      
      const videoContainer = this.container.querySelector('.card-media-video');
      if (videoContainer) videoContainer.style.display = 'none';
    }
  }

  colorSwatchFetch() {
    const productHandle = this.dataset.productHandle;
    const collectionHandle = this.dataset.collectionHandle || '';
    let sectionUrl = `${theme.routes.root_url}/products/${productHandle}?variant=${this.variantId}&view=card`;

    if (collectionHandle.length > 0) {
      sectionUrl = `${theme.routes.root_url}/collections/${collectionHandle}/products/${productHandle}?variant=${this.variantId}&view=card`;
    }

    sectionUrl = sectionUrl.replace(/([^:]\/)\/+/g, '$1'); // Clean duplicate slashes

    if (this.cached[sectionUrl]) {
      this.renderProductInfo(this.cached[sectionUrl]);
      return;
    }

    fetch(sectionUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.cached[sectionUrl] = html;
        this.renderProductInfo(html);
      })
      .catch((e) => {
        console.error('ColorSwatch fetch error:', e);
      });
  }

  renderProductInfo(html) {
    this.updateButtons(html);
    this.updateTitle(html);
    this.updatePrice(html);
    this.updateColorLabel(html);
    this.colorSwatchChange(true);
  }

  updateTitle(html) {
    const selector = '.card-information__text';
    const destination = this.container.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) {
      destination.innerHTML = source.innerHTML + '<small>&ndash;&nbsp;' + this.getAttribute('title') + '</small>';
    }
  }

  updatePrice(html) {
    if (html.querySelector('color-swatch-radios')) return;

    const selector = '.price';
    const destination = this.container.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  }

  updateButtons(html) {
    const selector = '.card-information__button';
    const destination = this.container.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) {
      destination.innerHTML = source.innerHTML;
      destination.classList.toggle('is-expanded', source.classList.contains('is-expanded'));
    }
  }

  updateColorLabel(html) {
    const activeSwatch = this.container.querySelector('.color-swatch.is-active');
    if (!activeSwatch) return;

    const colorLabel = this.container.querySelector('.color-swatch-label') || 
                      html.querySelector('.color-swatch-label');
    if (colorLabel) {
      colorLabel.textContent = activeSwatch.getAttribute('title');
    }
  }
}

class ColorSwatchRadios extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      soldOut: 'price--sold-out',
      onSale: 'price--on-sale',
      noCompare: 'price--no-compare',
      hidden: 'hidden',
      loading: 'loading'
    };

    this.selectors = {
      cardWrapper: '.card-wrapper',
      priceWrapper: '.price',
      price: '.price__regular .price-item--regular',
      comparePrice: '.price__sale .price-item--regular',
      salePrice: '.price__sale .price-item--sale',
      unitWrapper: '.unit-price',
      unitPrice: '[data-unit-price]',
      unitPriceBaseUnit: '[data-unit-base]',
      colorLabel: '.color-swatch-label'
    };

    this.miniCart = document.querySelector('mini-cart');
    this.productForm = this.closest('product-form');
    this.variantSelects = this.querySelector('variant-selects');

    this.init();
    this.setupEventListeners();
  }

  init() {
    if (!this.variantSelects) return;
    this.variantSelects.addEventListener('change', this.onVariantChange.bind(this));
  }

  setupEventListeners() {
    const buttons = this.querySelectorAll('.swatch:not(.disabled)');
    buttons.forEach((button) => {
      button.addEventListener('mouseenter', this.onEnterHandler.bind(this));
      button.addEventListener('click', this.onClickHandler.bind(this));
      button.addEventListener('focus', this.onEnterHandler.bind(this));
    });

    this.closest('.product')?.addEventListener('mediaChanged', this.onMediaChanged.bind(this));
  }

  onClickHandler(event) {
    if (theme.config.mqlSmall || theme.config.isTouch) {
      event.stopPropagation();
      this.onEnterHandler(event);
    }
  }

  onEnterHandler(event) {
    const swatch = event.currentTarget;
    if (!swatch) return;

    const input = swatch.querySelector('input');
    if (input && !input.checked) {
      input.checked = true;
      this.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onMediaChanged(event) {
    const mediaId = event.detail.mediaId;
    const variant = this.getVariantData()?.find((v) => v.featured_media?.id === mediaId);
    
    if (!variant) return;

    const input = this.querySelector(`input[value="${variant.id}"]`);
    if (input && !input.checked) {
      input.checked = true;
      this.dispatchEvent(new Event('change'));
      this.updateColorLabel(variant.id);
    }
  }

  onVariantChange() {
    const variantId = this.selectedValue;
    const variant = this.getVariantData().find((v) => v.id === parseInt(variantId));
    
    if (!variant) return;

    this.updateMasterSelect();
    this.updateURL();
    this.updateVariantInput();
    this.updatePrice(variant);
    this.updateUnitPrice(variant);
    this.updateSKU(variant);
    this.updateAvailability(variant);
    this.updateMedia(variant);
    this.updateColorLabel(variantId);
    
    this.miniCart?.refreshCart();
  }

  updateColorLabel(variantId) {
    const selectedInput = this.querySelector(`input[value="${variantId}"]`);
    if (!selectedInput) return;

    const swatch = selectedInput.closest('.color-swatch');
    const colorLabel = this.closest('.product')?.querySelector(this.selectors.colorLabel);
    
    if (swatch && colorLabel) {
      colorLabel.textContent = swatch.getAttribute('title');
    }
  }

  updateMedia(variant) {
    if (!variant?.featured_media) return;
    
    this.dispatchEvent(new CustomEvent('mediaChanged', {
      detail: {
        mediaId: variant.featured_media.id,
        variantId: variant.id
      }
    }));
    
    if (theme.config.mqlSmall || theme.config.isTouch) {
      const mediaGallery = document.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      mediaGallery?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  getVariantData() {
    return this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
  }

  get selectedValue() {
    return this.querySelector('input[name="color"]:checked')?.value;
  }

  updateMasterSelect() {
    if (!this.productForm) return;
    const masterSelect = this.productForm.querySelector('select[name="id"]');
    if (masterSelect) masterSelect.value = this.selectedValue;
  }

  updateURL() {
    if (!this.dataset.updateUrl || !this.selectedValue) return;
    window.history.replaceState({}, '', `${window.location.pathname}?variant=${this.selectedValue}`);
  }

  updateVariantInput() {
    const input = this.closest('product-form')?.querySelector('input[name="id"]');
    if (input) input.value = this.selectedValue;
  }

  updatePrice(variant) {
    const priceWrapper = this.querySelector(this.selectors.priceWrapper);
    if (!priceWrapper) return;

    priceWrapper.classList.toggle(this.classes.onSale, variant.compare_at_price > variant.price);
    priceWrapper.classList.toggle(this.classes.soldOut, !variant.available);

    const priceElement = priceWrapper.querySelector(this.selectors.price);
    const comparePriceElement = priceWrapper.querySelector(this.selectors.comparePrice);
    const salePriceElement = priceWrapper.querySelector(this.selectors.salePrice);

    if (priceElement) {
      priceElement.textContent = theme.formatMoney(variant.price);
    }
    if (comparePriceElement) {
      comparePriceElement.textContent = theme.formatMoney(variant.compare_at_price);
    }
    if (salePriceElement) {
      salePriceElement.textContent = theme.formatMoney(variant.price);
    }
  }

  updateUnitPrice(variant) {
    const unitWrapper = this.querySelector(this.selectors.unitWrapper);
    if (!unitWrapper || !variant.unit_price_measurement) {
      unitWrapper?.classList.add(this.classes.hidden);
      return;
    }

    unitWrapper.classList.remove(this.classes.hidden);
    const unitPriceElement = unitWrapper.querySelector(this.selectors.unitPrice);
    const unitBaseElement = unitWrapper.querySelector(this.selectors.unitPriceBaseUnit);

    if (unitPriceElement) {
      unitPriceElement.textContent = theme.formatMoney(variant.unit_price);
    }
    if (unitBaseElement) {
      unitBaseElement.textContent = [
        variant.unit_price_measurement.reference_value,
        variant.unit_price_measurement.reference_unit
      ].join('');
    }
  }

  updateSKU(variant) {
    const skuElement = this.querySelector('.product-sku');
    if (skuElement) {
      skuElement.textContent = variant.sku || '';
    }
  }

  updateAvailability(variant) {
    const availabilityElement = this.querySelector('.product-availability');
    if (availabilityElement) {
      availabilityElement.textContent = variant.available ? 'In stock' : 'Out of stock';
      availabilityElement.classList.toggle('text-success', variant.available);
      availabilityElement.classList.toggle('text-error', !variant.available);
    }
  }
}

customElements.define('color-swatch', ColorSwatch);
customElements.define('color-swatch-radios', ColorSwatchRadios);
